/* eslint-disable no-underscore-dangle */
/**
 * WHERE THE FINAL SZÁMLA'S PRE-FILLED VALUES CAME FROM, and how the booking
 * has moved since.
 *
 * A végszámla is a copy task. Measured over Gabriella's documents on
 * 2026-11-02: 282 of 295 are fully derivable from the Előlegszámla that
 * preceded them — 285/285 identical partner account (zero divergences),
 * 285/285 identical currency, 285/285 identical amount, 282/285 identical
 * booking set. All 13 exceptions are detectable BEFORE issuing (10 have no
 * advance to copy from, 3 have a booking set that moved).
 *
 * WHY THE 96% IS SAFE, and it is only safe for this reason: the 4% announces
 * itself first. Every value shown carries the source it came from, and
 * `orderLineDrift` compares the predecessor's lines against the booking's
 * order lines as they stand NOW — they matched in 286 of 295, so a mismatch
 * is a real signal, not noise. A HUMAN STILL PRESSES ISSUE. Nothing here
 * auto-issues, and nothing here should: the pre-fill buys the assembly, not
 * the decision.
 *
 * WHY THE SOURCES ARE DERIVED AND NOT ASSERTED. Every attribution below is a
 * comparison against the value the derivation would have produced, so a field
 * the operator edited reads as edited. A panel that claimed "copied from the
 * Előlegszámla" over a hand-typed value would join the list of things these
 * screens say that are not true, which is the problem this work is about.
 */

import {
  DOC_TYPES, isLiveDocument, lineGross, lineKey,
} from './billingoDocuments';

/**
 * The document this one is derived from: for a Számla the newest live
 * Előlegszámla, falling back to the newest live Díjbekérő (which carries the
 * same party and currency and is what exists when no advance was taken).
 * `null` when there is no predecessor — the 10 of 295 with nothing to copy.
 *
 * Only a Számla has a predecessor worth naming. An Előlegszámla's own
 * predecessor is a Díjbekérő, but the flow already reuses its account through
 * `priorAccountId`, and an advance is not a copy of the request.
 */
export function predecessorDocument(documents = [], docType = null) {
  if (docType !== DOC_TYPES.SZAMLA) return null;
  const live = (documents || [])
    .filter((d) => d && isLiveDocument(d))
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  return live.find((d) => d.type === DOC_TYPES.ELOLEGSZAMLA)
    || live.find((d) => d.type === DOC_TYPES.DIJBEKERO)
    || null;
}

/**
 * Where each silently pre-filled value came from, as a code per field. Only
 * the three values the review step shows WITHOUT an origin are attributed:
 * the account, the currency and the fulfillment date. The payment picker, the
 * due date, the method and the language are visible, editable controls — an
 * origin line on those is noise, not provenance.
 *
 * Codes: 'predecessor' | 'partner' | 'order' | 'default' | 'edited' | 'picked'
 * | 'none'.
 */
export function prefillSources({
  predecessor = null, account = null, partner = null,
  currency = null, orderCurrency = null,
  meta = {}, metaDefaults = {},
}) {
  // 'partner' is reported, never chosen here: see the measurement in
  // BillingoDocumentFlow for why a partner's account must not be used as a
  // default beyond the one it has explicitly configured (383 distinct
  // accounts on the house partner alone, 2026-11-02).
  let accountSource = 'none';
  if (account) {
    if (predecessor && account.id && account.id === predecessor.billingo_account_id) {
      accountSource = 'predecessor';
    } else if (partner?.billingo_account_id && account.id === partner.billingo_account_id) {
      accountSource = 'partner';
    } else {
      accountSource = 'picked';
    }
  }

  let currencySource = 'picked';
  if (predecessor && currency && currency === predecessor.currency) currencySource = 'predecessor';
  else if (currency && currency === orderCurrency) currencySource = 'order';

  const fulfillmentSource = meta.fulfillment_date
    && meta.fulfillment_date === metaDefaults.fulfillment_date
    ? 'default'
    : 'edited';

  return { account: accountSource, currency: currencySource, fulfillment_date: fulfillmentSource };
}

/** Gross per composite line key on a stored document's lines. */
const predecessorGrossByKey = (predecessor) => {
  const out = new Map();
  (predecessor?.line_items || []).forEach((line) => {
    if (!line || line.deduction || !line.booking_id || !line.order_line_key) return;
    const key = lineKey(line.booking_id, line.order_line_key);
    const gross = Number(line.gross);
    out.set(key, (out.get(key) || 0) + (Number.isFinite(gross) ? gross : 0));
  });
  return out;
};

/**
 * HOW THE BOOKING HAS MOVED SINCE THE PREDECESSOR WAS ISSUED — the 3% signal.
 *
 * Compares the predecessor's billed lines against the candidate lines built
 * from the booking's order_lines right now, on the composite
 * `${booking_id}|${order_line_key}` the document already stores its
 * `line_breakdown` under. Returns
 *   { comparable, added: [item], removed: [{ key, gross }], changed: [...] }
 *
 * `comparable: false` means the comparison could not be made and nothing
 * should be claimed: a predecessor issued before `order_line_key` was stored
 * on document lines has no key to match on, and every line would otherwise
 * read as removed-and-re-added. Saying "cannot compare" is the honest answer;
 * showing nine false mismatches would teach the operator to ignore the panel.
 *
 * Only candidates in the document's currency are compared — a line in another
 * currency is not billable on this document at all, so its absence from the
 * predecessor is not drift.
 */
export function orderLineDrift(predecessor, candidates = [], currency = null) {
  const empty = {
    comparable: false, added: [], removed: [], changed: [],
  };
  if (!predecessor) return empty;

  const billed = (predecessor.line_items || []).filter((l) => l && !l.deduction);
  if (!billed.length || billed.some((l) => !l.order_line_key)) return empty;

  const before = predecessorGrossByKey(predecessor);
  const now = (candidates || [])
    .filter((c) => c && (!currency || c.currency === currency))
    .filter((c) => !c.deduction);

  const added = [];
  const changed = [];
  const seen = new Set();
  now.forEach((item) => {
    seen.add(item.key);
    const was = before.get(item.key);
    const is = lineGross(item);
    if (was === undefined) {
      added.push(item);
    } else if (Math.abs(was - is) > 0.005) {
      changed.push({
        key: item.key,
        order_reference: item.order_reference,
        comment: item.comment,
        from: was,
        to: is,
      });
    }
  });

  const removed = [...before.entries()]
    .filter(([key]) => !seen.has(key))
    .map(([key, gross]) => {
      const line = billed.find((l) => lineKey(l.booking_id, l.order_line_key) === key);
      return {
        key, gross, order_reference: line?.order_reference, comment: line?.comment,
      };
    });

  return {
    comparable: true, added, removed, changed,
  };
}

/** True when `orderLineDrift` found something worth showing. */
export const hasDrift = (drift) => Boolean(
  drift && drift.comparable
  && (drift.added.length || drift.removed.length || drift.changed.length),
);
